import React, { useRef, useCallback } from 'react'
import classnames from 'classnames/bind'
import { gsap } from 'gsap'
import { createGroupRefs } from 'services/helpers'
import { Title, Label } from 'components/typo'
import Separator from 'components/shared/Separator'
import RichText from 'components/typo/RichText'

import useTimeline from 'hooks/useTimeline'
import useAnimateInView from 'hooks/useAnimateInView'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export default function Question({ className, title, answer, index }) {
  const elementRef = useRef()
  const questionRef = useRef()
  const answerRef = useRef()
  const pastilleRef = useRef()
  const separatorRef = useRef()

  const tl = useTimeline({ paused: true, delay: 0.2 }, (tl) => {
    gsap.set(elementRef.current, {
      opacity: 1,
    })

    const group = createGroupRefs([
      [title, questionRef.current],
      [answer, answerRef.current],
    ])

    const stagger = 0.1

    tl.fromTo(
      group,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: 'none',
        stagger,
        force3D: true,
        duration: 0.3,
      }
    )

    tl.fromTo(
      group,
      {
        y: 80,
      },
      {
        y: 0,
        stagger,
        duration: 1,
        force3D: true,
        ease: 'elastic.out(0.3,0.2)',
      },
      '<'
    )

    tl.fromTo(
      pastilleRef.current,
      { opacity: 0 },
      { opacity: 1, ease: 'none', duration: 0.2 },
      '>-0.8'
    )

    tl.fromTo(
      pastilleRef.current,
      {
        z: -400,
      },
      {
        z: 0,
        duration: 0.8,
        ease: 'elastic.out(0.4, 0.2)',
      },
      '<'
    )

    tl.fromTo(
      separatorRef.current,
      { opacity: 0 },
      { opacity: 1, ease: 'none', duration: 0.3 },
      '-=0.6'
    )
  })

  const [inViewRef] = useAnimateInView(
    {
      triggerOnce: true,
      animateIn: () => tl.restart(true),
    },
    []
  )

  const setRef = useCallback((node) => {
    elementRef.current = node
    inViewRef(node)
  }, [])

  return (
    <div ref={setRef} className={cx(css.Question, className)}>
      <div ref={questionRef} className={css.title}>
        <div className={css.pastilleContainer}>
          <div ref={pastilleRef} className={css.pastille}>
            <Label theme="purple">Q{index + 1}.</Label>
          </div>
        </div>
        {title && (
          <Title tag="h3" theme="bay-blue" size="small">
            {title}
          </Title>
        )}
      </div>
      {answer && (
        <div ref={answerRef}>
          <RichText
            type="Text"
            text={answer}
            tag="div"
            className={css.answer}
          />
        </div>
      )}
      <Separator forwardRef={separatorRef} className={css.separator} />
    </div>
  )
}
